import { BookingAddOnPaymentDto } from '@hqo/resource-booking-client';

import { amendRefusal } from 'store/api-client/add-on-payment-types';

/** What a tenant can do about an order they have already placed. */
export type AddOnOrderAction = 'pay' | 'cancel' | 'amend' | 'reorder';

/**
 * The service says this order no longer exists: it was cancelled, or cancelled and paid back.
 *
 * `REFUNDED` counts: once the refund lands the reason moves off `ORDER_CANCELLED`, so testing that
 * one value alone reports a cancelled-and-refunded order as live.
 */
export const isAddOnOrderGone = (payment: BookingAddOnPaymentDto | null | undefined): boolean =>
  payment?.cannotPayReason === 'ORDER_CANCELLED' || payment?.cannotPayReason === 'REFUNDED';

/**
 * Everything the service told us about this order, in the shape the controls need it.
 *
 * `OrderedAddOnsRead` minus `cannotOrderReason`, declared here rather than imported so the
 * component layer does not reach into `data/queries`; the hook's object satisfies it structurally.
 */
export interface AddOnOrderGate {
  payment: BookingAddOnPaymentDto | null;
  /**
   * The booking's verdict: may it take any further add-on order at all. A cancellation awaiting
   * confirmation turns it false for every vendor on the booking at once.
   */
  canOrderAddOns: boolean;
  /**
   * This work order's verdict: why it can no longer be changed, or null when it still can. Free
   * text from work-orders -- see `amendRefusal` for why it is not an enum.
   */
  amendRefusedReason: string | null;
}

export interface AddOnOrderActions {
  actions: AddOnOrderAction[];
  /**
   * The vendor has picked the job up, so nothing the tenant does from here reaches them.
   *
   * Reported separately from an empty action list because the two mean different things to a
   * reader: nothing offered because the order is settled is silence, and nothing offered because
   * somebody is already working on it is worth a sentence.
   */
  isVendorUnderway: boolean;
}

const NOTHING: AddOnOrderActions = { actions: [], isVendorUnderway: false };

/*
 * The one status that means "nobody has touched this yet".
 *
 * ⚠️ COMPARED LOOSELY ON PURPOSE. `workOrderStatus` is a status NAME, and work-orders lets a
 * building rename its statuses and bulk-provision custom ones carrying free text -- its own
 * `product-add-on-lock-timing.md` §2.5 says the name is unreliable and resolves by the status'
 * sourceId lineage instead, which the tenant read does not expose. So an unrecognised name is NOT
 * read as "started": it is read as "we were not told", and the money flags below decide alone.
 */
const NOT_STARTED = 'NEW';

/**
 * What the tenant may do about an add-on order, and whether the vendor has moved on it.
 *
 * ⛔ THE FLAGS DECIDE, NOT THIS. Every rule below is work-orders' answer read back, never
 * re-derived: `isPayable` is "will billing still take a charge against that session", and
 * `isCancellableWithRefund` is "would a cancel actually return the money". Both already fold in
 * things this app cannot see -- session expiry, the five-day TTL, a failed charge, the vendor
 * having started -- so re-deciding any of it here is how the button and the endpoint come to
 * disagree.
 *
 * ⚠️ MUTUALLY EXCLUSIVE BY CONSTRUCTION, in the service, not by the order of these branches. An
 * order is awaiting a price, or payable, or paid-and-refundable: work-orders cannot answer true to
 * two of them, and RBS#189 forces all three false while a cancellation is outstanding. The order
 * here is for the reader.
 *
 * ⭐ AND THE EMPTY ANSWER IS A REAL ONE. A settled order, a cancelled one, an expired session with
 * no way to re-issue it: all of them offer nothing, and offering something anyway is how a tenant
 * presses a control the backend was always going to refuse.
 */
export const addOnOrderActions = (gate: AddOnOrderGate | null): AddOnOrderActions => {
  const payment = gate?.payment ?? null;

  /*
   * ⚠️ Not "no actions because there is nothing to do" -- we were told nothing at all. The payment
   * read sits behind a permission and answers null when work-orders is unreachable, so this is an
   * absence of information, and inventing an action over it would be worse than offering none.
   */
  if (!payment) {
    return NOTHING;
  }

  const isVendorUnderway = Boolean(payment.workOrderStatus) && payment.workOrderStatus !== NOT_STARTED;

  /*
   * Three gates: whether work-orders would take the change, whether the booking can order at all, and
   * whether an attempt was already refused.
   *
   * `canAmend` is READ, never derived. Deriving it from `isCancellableWithRefund` reads that flag as
   * "settled", and it is true for an unpaid order the vendor has not started - exactly the order
   * work-orders amends happily. work-orders resolves it through `decideAmend`, the section 4 matrix.
   *
   * The refusal is tested `=== null` rather than against known reasons: it is free text on the wire,
   * and `amendRefusal` answers `'unknown'` for a word this build has never seen.
   */
  const canAmend = payment.canAmend && Boolean(gate?.canOrderAddOns) && amendRefusal(gate?.amendRefusedReason) === null;

  /* Still being priced: there is no figure to pay, but the tenant can change or stand down the order. */
  if (payment.isPaymentStatePending) {
    return { actions: canAmend ? ['amend', 'cancel'] : ['cancel'], isVendorUnderway };
  }

  /* Priced and unpaid, with a session billing will still take. */
  if (payment.isPayable) {
    return { actions: canAmend ? ['pay', 'amend', 'cancel'] : ['pay', 'cancel'], isVendorUnderway };
  }

  /*
   * Paid, and the vendor has not started -- so a cancel returns the money. Section 4 amends this one
   * too, by refunding in full and re-charging the new total, so the change is offered when the service
   * allows it rather than refused here. `reorder` is not offered beside a live order; that is below.
   */
  if (payment.isCancellableWithRefund) {
    return { actions: canAmend ? ['amend', 'cancel'] : ['cancel'], isVendorUnderway };
  }

  /*
   * An order that is gone is not an order that is settled. `canOrderAddOns` is the whole gate:
   * `CANNOT_ORDER_REASON` has no "already cancelled" value, and once work-orders confirms the
   * cancellation RBS revives the link so a fresh submit creates a new vendor request.
   *
   * `reorder` rather than `amend`: RBS suppresses `amendRefusedReason` for a cancelled link, and
   * there is nothing left to modify.
   */
  if (isAddOnOrderGone(payment)) {
    return { actions: gate?.canOrderAddOns ? ['reorder'] : [], isVendorUnderway };
  }

  /* An expired session with no way to re-issue it: nothing to pay and nothing to refund, but an
   * unpaid order the service may still take an amend on. A settled one has already failed `canAmend`. */
  return { actions: canAmend ? ['amend'] : [], isVendorUnderway };
};
